import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiService } from '../../services/apiService';
import { SearchParameterType } from '../../types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';

interface SimpleRapidAPIImportProps {
  onImport: (apiData: any) => void;
  onCancel: () => void;
}

interface PreviewData {
  api_data: any;
  parameter_type: {
    id: number;
    name: string;
    display_name: string;
  };
  organization: {
    id: number;
    name: string;
    description: string;
  };
  validation: {
    valid: boolean;
    errors: string[];
  };
}

export const SimpleRapidAPIImport: React.FC<SimpleRapidAPIImportProps> = ({
  onImport,
  onCancel
}) => {
  const [content, setContent] = useState('');
  const [parameterType, setParameterType] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);

  // Example RapidAPI code
  const exampleCode = `import requests

url = "https://website-social-scraper-api.p.rapidapi.com/contacts"

querystring = {"website":"https://www.apple.com/"}

headers = {
    "x-rapidapi-key": "bc15878f7dmshe5d586b09c2a3dfp10dd3ajsnb900000e139b",
    "x-rapidapi-host": "website-social-scraper-api.p.rapidapi.com"
}

response = requests.get(url, headers=headers, params=querystring)

print(response.json())`;

  // Load parameter types on component mount
  useEffect(() => {
    const loadParameterTypes = async () => {
      try {
        setIsLoadingTypes(true);
        const types = await apiService.getParameterTypes();
        setParameterTypes(types);
      } catch (error) {
        console.error('Failed to load parameter types:', error);
        toast.error('Failed to load parameter types');
      } finally {
        setIsLoadingTypes(false);
      }
    };

    loadParameterTypes();
  }, []);

  const handlePreview = async () => {
    if (!content.trim()) {
      toast.error('Please paste RapidAPI code');
      return;
    }

    if (!parameterType) {
      toast.error('Please select a parameter type');
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await apiService.adminRequest('/admin/api-details/preview_rapidapi/', {
        method: 'POST',
        body: JSON.stringify({
          content: content.trim(),
          parameter_type_id: parseInt(parameterType)
        })
      });

      if ((response as any).success) {
        setPreviewData((response as any));
        setShowPreview(true);
        toast.success('Preview generated successfully');
      } else {
        toast.error((response as any).message || 'Preview failed');
      }
    } catch (error) {
      console.error('RapidAPI preview error:', error);
      toast.error('Preview failed. Please check your code format.');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!previewData) {
      toast.error('Please generate preview first');
      return;
    }

    setIsImporting(true);
    try {
      const response = await apiService.adminRequest('/admin/api-details/import_rapidapi/', {
        method: 'POST',
        body: JSON.stringify({
          content: content.trim(),
          parameter_type_id: parseInt(parameterType)
        })
      });

      if ((response as any).success) {
        toast.success('API imported successfully!');
        onImport((response as any).api_data);
      } else {
        toast.error((response as any).message || 'Import failed');
      }
    } catch (error) {
      console.error('RapidAPI import error:', error);
      toast.error('Import failed. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleBackToEdit = () => {
    setShowPreview(false);
    setPreviewData(null);
  };

  const formatJson = (obj: any): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const loadExample = () => {
    setContent(exampleCode);
    // Auto-select website parameter type if available
    const websiteType = parameterTypes.find(t => t.name === 'website');
    if (websiteType) {
      setParameterType(websiteType.id.toString());
    }
  };

  if (showPreview && previewData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Preview</h3>
          <Button onClick={handleBackToEdit} variant="outline" size="sm">
            Back to Edit
          </Button>
        </div>

        <Card>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">API Information</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Name:</span> {previewData.api_data.name}</div>
                  <div><span className="font-medium">Description:</span> {previewData.api_data.description}</div>
                  <div><span className="font-medium">Base URL:</span> {previewData.api_data.base_url}</div>
                  <div><span className="font-medium">Method:</span> {previewData.api_data.request_method}</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Configuration</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Parameter Type:</span> {previewData.parameter_type.display_name}</div>
                  <div><span className="font-medium">Organization:</span> {previewData.organization.name}</div>
                  <div><span className="font-medium">Parameter Format:</span> {previewData.api_data.parameter_format}</div>
                  <div><span className="font-medium">Parameter Name:</span> {previewData.api_data.parameter_name}</div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Headers</h4>
              <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs overflow-x-auto">
                {formatJson(previewData.api_data.headers)}
              </pre>
            </div>

            {previewData.api_data.request_parameters && Object.keys(previewData.api_data.request_parameters).length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Request Parameters</h4>
                <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs overflow-x-auto">
                  {formatJson(previewData.api_data.request_parameters)}
                </pre>
              </div>
            )}

            {previewData.validation && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Validation</h4>
                {previewData.validation.valid ? (
                  <Badge variant="success">Valid Configuration</Badge>
                ) : (
                  <div className="space-y-1">
                    <Badge variant="error">Configuration Issues</Badge>
                    <ul className="text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                      {previewData.validation.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <Button
                onClick={handleImport}
                disabled={isImporting || !previewData.validation?.valid}
                className="flex-1"
              >
                {isImporting ? 'Importing...' : 'Import API'}
              </Button>
              <Button onClick={handleBackToEdit} variant="outline" className="flex-1">
                Back to Edit
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Import RapidAPI</h3>
        <p className="text-gray-600 dark:text-gray-300">
          Paste your RapidAPI code and we'll automatically configure the API for you.
        </p>
      </div>

      <Card>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              RapidAPI Code
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste your RapidAPI code here..."
              className="w-full h-48 p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isPreviewing}
            />
            <div className="mt-2 flex justify-between items-center">
              <button
                type="button"
                onClick={loadExample}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 underline"
              >
                Load Example
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {content.length} characters
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Parameter Type
            </label>
            {isLoadingTypes ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading parameter types...</div>
            ) : (
              <select
                value={parameterType}
                onChange={(e) => setParameterType(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isPreviewing}
              >
                <option value="">Select parameter type</option>
                {parameterTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.display_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handlePreview}
              disabled={isPreviewing || !content.trim() || !parameterType}
              className="flex-1"
            >
              {isPreviewing ? 'Generating Preview...' : 'Generate Preview'}
            </Button>
            <Button onClick={onCancel} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </Card>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Supported Format</h4>
        <p className="text-sm text-blue-800 mb-3">
          This importer works with standard RapidAPI code using the requests library. 
          It automatically extracts URLs, headers, parameters, and request methods.
        </p>
        <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
          <div>• Supports GET, POST, PUT, DELETE requests</div>
          <div>• Extracts query parameters, form data, and JSON body</div>
          <div>• Automatically configures RapidAPI headers</div>
          <div>• Generates appropriate API names and descriptions</div>
        </div>
      </div>
    </div>
  );
}; 